import { SiteFooter } from "@/components/SiteFooter";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Trophy, Music, Mic, Calendar, UtensilsCrossed, Clock, ArrowRight } from "lucide-react";
import { listPublishedEvents } from "@/lib/events.functions";
import { useTrackEvent } from "@/lib/analytics";

export const Route = createFileRoute("/events/")({
  head: () => {
    const ogImage = "https://plunkets.ie/og-events.jpg?v=2";
    const url = "https://plunkets.ie/events";
    return {
      meta: [
        { title: "Live Sport Pub Dublin City Centre — What's On | Plunkets" },
        { name: "description", content: "The sport pub in Dublin city centre. Live Premier League, GAA, hurling, World Cup football, international rugby, UFC and F1 on 12 large TVs, plus karaoke and live music at Plunkets, Middle Abbey Street." },
        { name: "keywords", content: "sport pub Dublin, sports pub Dublin city centre, live sport Dublin, GAA pub Dublin, Premier League pub Dublin, UFC pub Dublin, karaoke Dublin city centre" },
        { property: "og:title", content: "Live Sport Pub in Dublin City Centre — Plunkets" },
        { property: "og:description", content: "Every match on every screen. Karaoke, live music and event nights in Dublin city centre." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:image", content: ogImage },
        { property: "og:image:secure_url", content: ogImage },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:alt", content: "Live sport and events at Plunkets Pub Dublin" },
        { property: "og:locale", content: "en_IE" },
        { property: "og:site_name", content: "Plunkets Pub Dublin" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Live Sport Pub in Dublin City Centre — Plunkets" },
        { name: "twitter:description", content: "Every match on every screen. Karaoke and live music at Plunkets Dublin." },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: EventsPage,
});

const recurring = [
  { icon: Trophy, title: "Sport On Every Day", desc: "Premier League, GAA & hurling (including retro/old classics), World Cup football, international rugby, UFC pay-per-views and F1 — shown daily on 12 large TVs with proper sound." },
  { icon: Mic, title: "Karaoke Nights — Thursday & Sunday", desc: "10,000-song catalogue and pro mics on the Karaoke Terrace. Free entry. Book ahead for groups." },
  { icon: Music, title: "Live Music — Thursday to Sunday", desc: "Local Dublin acts, trad sessions and DJ sets Thu–Sun (7 nights a week after the World Cup). Check What's On for this week's line-up." },
];

function iconForTag(tag: string | null) {
  const t = (tag ?? "").toLowerCase();
  if (t.includes("sport") || t.includes("match")) return Trophy;
  if (t.includes("music") || t.includes("dj") || t.includes("trad")) return Music;
  if (t.includes("karaoke")) return Mic;
  if (t.includes("food") || t.includes("special")) return UtensilsCrossed;
  return Calendar;
}

function formatWhen(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("en-IE", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function EventsPage() {
  useTrackEvent("event_page_view", { page_type: "events_index" });
  const listFn = useServerFn(listPublishedEvents);
  const { data: events = [] } = useQuery({
    queryKey: ["public-events"],
    queryFn: () => listFn({ data: { limit: 24 } }),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const [activeTag, setActiveTag] = useState<string>("All");
  const tags = useMemo(() => {
    const set = new Set<string>();
    events.forEach((e) => e.tag && set.add(e.tag));
    return ["All", ...Array.from(set)];
  }, [events]);
  const filtered = useMemo(
    () => (activeTag === "All" ? events : events.filter((e) => e.tag === activeTag)),
    [events, activeTag],
  );

  const featured = events.find((e) => e.banner_url) ?? events[0];

  return (
    <main className="min-h-screen bg-background text-foreground">
      {featured?.banner_url && (
        <section className="relative h-[52vh] min-h-[360px] w-full overflow-hidden">
          <img src={featured.banner_url} alt="Live sport and events at Plunkets Dublin" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
          <div className="relative z-10 mx-auto flex h-full max-w-5xl flex-col justify-end px-5 pb-12 md:px-8 md:pb-16">
            <p className="text-xs uppercase tracking-[0.4em] text-primary">What's On</p>
            <h1 className="mt-4 font-display text-4xl leading-[0.95] tracking-tight md:text-6xl">
              Live Sport & <span className="text-gold-gradient">Events</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
              Every match, every screen. Live music, karaoke and event nights — Plunkets is where Dublin city centre
              shows up for the big games and the late ones.
            </p>
          </div>
        </section>
      )}

      <div className="px-5 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-5xl">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft size={16} /> Back to Plunkets
          </Link>

          {!featured?.banner_url && (
            <>
              <p className="mt-10 text-xs uppercase tracking-[0.4em] text-primary">What's On</p>
              <h1 className="mt-4 font-display text-4xl leading-[0.95] tracking-tight md:text-6xl">
                Live Sport & <span className="text-gold-gradient">Events</span>
              </h1>
              <p className="mt-6 max-w-2xl text-base text-muted-foreground md:text-lg">
                Every match, every screen. Live music, karaoke and event nights — Plunkets is where Dublin city centre
                shows up for the big games and the late ones.
              </p>
            </>
          )}


          {events.length > 0 && (
            <div className="mt-12">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-display text-2xl font-bold md:text-3xl">Upcoming events</h2>
                <span className="text-xs text-muted-foreground">{filtered.length} of {events.length}</span>
              </div>

              {/* Filter chips */}
              <div className="mt-5 flex flex-wrap gap-2">
                {tags.map((t) => {
                  const active = activeTag === t;
                  return (
                    <button
                      key={t}
                      onClick={() => setActiveTag(t)}
                      className={
                        "rounded-full border px-4 py-1.5 text-xs uppercase tracking-wider transition " +
                        (active
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-card/40 text-muted-foreground hover:border-primary/50 hover:text-foreground")
                      }
                    >
                      {t}
                    </button>
                  );
                })}
              </div>

              {filtered.length === 0 ? (
                <p className="mt-10 text-sm text-muted-foreground">No events for "{activeTag}" yet.</p>
              ) : (
                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filtered.map((e) => {
                    const Icon = iconForTag(e.tag);
                    return (
                      <Link
                        key={e.id}
                        to="/events/$slug"
                        params={{ slug: e.slug ?? e.id }}
                        className="group block overflow-hidden rounded-2xl border border-border bg-card/30 transition hover:border-primary/50"
                      >
                        {e.banner_url ? (
                          <img src={e.banner_url} alt={e.title} className="aspect-[16/9] w-full object-cover transition group-hover:scale-[1.02]" loading="lazy" />
                        ) : (
                          <div className="flex aspect-[16/9] w-full items-center justify-center bg-muted/30 text-muted-foreground">
                            <Icon size={28} />
                          </div>
                        )}
                        <div className="p-5">
                          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary">
                            <Icon size={13} /> {e.tag ?? "Event"}
                          </div>
                          <h3 className="mt-2 font-display text-xl font-semibold leading-tight">{e.title}</h3>
                          <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Clock size={12} /> {formatWhen(e.starts_at)}
                          </p>
                          {e.notes && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{e.notes}</p>}
                          <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                            View event <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="mt-16 space-y-4">
            <h2 className="font-display text-2xl font-bold md:text-3xl">Every week at Plunkets</h2>
            {recurring.map(({ icon: Icon, title, desc }) => (
              <article key={title} className="rounded-2xl border border-border bg-card/30 p-6 md:p-8">
                <Icon size={22} className="text-primary" />
                <h3 className="mt-3 font-display text-xl font-bold md:text-2xl">{title}</h3>
                <p className="mt-2 text-sm text-muted-foreground md:text-base">{desc}</p>
              </article>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-primary/30 bg-primary/5 p-6 md:p-8">
            <h2 className="font-display text-2xl font-bold md:text-3xl">Booking a group?</h2>
            <p className="mt-2 text-sm text-muted-foreground md:text-base">
              Birthdays, work socials, stags and match-day groups — we'll sort the table, the screens and the round.
            </p>
            <Link
              to="/"
              hash="groups"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold uppercase tracking-wider text-primary-foreground transition-all hover:opacity-90"
            >
              Group Bookings
            </Link>
          </div>
        </div>
      </div>
    <SiteFooter />
    </main>
  );
}
